import { Box, Flex, Icon, Image, Stack, Text } from '@chakra-ui/react';
import type { RegistrationProgressResponse } from '@diamond/shared/features/registration/browser';
import type { RegistrationVerificationState } from '@diamond/shared/features/registration/browser';
import {
  PersonOutlined,
  RestaurantOutlined,
} from '@mui/icons-material';
import { type ReactNode } from 'react';

import {
  type RegistrationOutlet,
  type RegistrationOutletAttachment,
  attachmentHasImagePreview,
  DESKTOP_ATTACHMENT_META,
  displayValue,
  getOutletAttachmentLabel,
  joinName,
} from './NooRegistrationStatusConstants';
import { NooStatusDocsIcon, NooStatusOutletIcon } from './NooRegistrationStatusIcons';
import { StatusBadge } from './NooRegistrationStatusSharedUi';

export function DetailField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <Box>
      <Text fontSize="12px" lineHeight="14px" color="#8E8E93">
        {label}
      </Text>
      <Text mt="2px" fontSize="16px" lineHeight="24px" color="#373F41">
        {displayValue(value)}
      </Text>
    </Box>
  );
}

function DetailAttachmentIcon({
  type,
}: {
  type: (typeof DESKTOP_ATTACHMENT_META)[number]['type'];
}) {
  if (type === 'pic_photo') {
    return <Icon as={PersonOutlined} boxSize={6} color="#0941A8" />;
  }

  if (type === 'outlet_photo') {
    return <NooStatusOutletIcon />;
  }

  if (type === 'kitchen_photo') {
    return <Icon as={RestaurantOutlined} boxSize={6} color="#0941A8" />;
  }

  return <NooStatusDocsIcon />;
}

export function DetailAttachmentCard({
  label,
  attachment,
  image,
  type,
}: {
  label: string;
  attachment: RegistrationOutletAttachment | undefined;
  image: boolean;
  type: (typeof DESKTOP_ATTACHMENT_META)[number]['type'];
}) {
  const hasFile = Boolean(attachment?.file_url);
  const showImagePreview = image || attachmentHasImagePreview(attachment);
  const statusLabel = showImagePreview
    ? hasFile
      ? '1 foto diupload'
      : 'Belum ada file diupload'
    : attachment?.original_filename || 'Belum ada file diupload';

  return (
    <Box
      border="1px solid"
      borderColor="#0000001A"
      borderRadius="10px"
      bg="#FFFFFF"
      p={4}
    >
      <Flex align="center" gap={4}>
        <Flex
          w="48px"
          h="48px"
          shrink={0}
          align="center"
          justify="center"
          borderRadius="8px"
          bg="#F1F8FF"
        >
          <DetailAttachmentIcon type={type} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text
            fontSize="18px"
            fontWeight="medium"
            lineHeight="27px"
            color="#373F41"
          >
            {label}
          </Text>
          <Text
            fontSize="14px"
            lineHeight="20px"
            color={hasFile ? '#007AFF' : '#666666'}
            noOfLines={2}
          >
            {statusLabel}
          </Text>
        </Box>
      </Flex>
      {showImagePreview ? (
        <Flex
          mt={4}
          h="167px"
          align="center"
          justify="center"
          borderRadius="8px"
          bg="#F1F8FF"
          overflow="hidden"
        >
          {hasFile ? (
            <Image
              src={attachment?.file_url ?? undefined}
              alt={label}
              w="full"
              h="full"
              objectFit="cover"
            />
          ) : null}
        </Flex>
      ) : null}
    </Box>
  );
}

export function MobileOutletDetailContent({
  outlet,
  picName,
  verificationState,
}: {
  outlet: RegistrationOutlet;
  picName: string | null;
  verificationState: RegistrationVerificationState;
}) {
  return (
    <Stack spacing={6}>
      <Box>
        <Flex align="center" gap={3} flexWrap="wrap">
          <Text
            fontSize="24px"
            lineHeight="29px"
            fontWeight="medium"
            color="#373F41"
          >
            {displayValue(outlet.name)}
          </Text>
          {outlet.is_primary ? (
            <Text
              px={3}
              py={1}
              borderRadius="full"
              bg="#F0FFF4"
              color="#34C759"
              fontSize="12px"
              lineHeight="15px"
              fontWeight="medium"
            >
              Outlet Utama
            </Text>
          ) : null}
        </Flex>
        <Flex mt={3} align="center" justify="space-between" gap={3}>
          <Text fontSize="16px" lineHeight="24px" color="#373F41">
            PIC: {displayValue(picName)}
          </Text>
          <StatusBadge state={verificationState} />
        </Flex>
      </Box>

      <DetailField label="Alamat" value={outlet.address} />

      <Stack spacing={3}>
        {DESKTOP_ATTACHMENT_META.map((item) => (
          <DetailAttachmentCard
            key={item.type}
            label={getOutletAttachmentLabel(item, outlet)}
            image={item.image}
            type={item.type}
            attachment={outlet.attachments.find(
              (attachment) => attachment.attachment_type === item.type
            )}
          />
        ))}
      </Stack>
    </Stack>
  );
}

type RegistrationBusinessProfile = NonNullable<
  RegistrationProgressResponse['business']
>;

export function BusinessVerificationDetailBody({
  business,
  afterHeading,
}: {
  business: RegistrationBusinessProfile;
  afterHeading?: ReactNode;
}) {
  const isCompany = business.ownership_type === 'company';
  const businessName = isCompany
    ? business.company_name
    : joinName(business.owner_first_name, business.owner_last_name);
  const personName = isCompany
    ? joinName(business.pic_first_name, business.pic_last_name)
    : joinName(business.owner_first_name, business.owner_last_name);
  const businessAddress = isCompany
    ? business.company_address
    : business.owner_address;
  const personAddress = isCompany
    ? business.pic_address
    : business.owner_address;
  const email = isCompany ? business.pic_email : business.owner_email;
  const phone = isCompany ? business.pic_phone : business.owner_phone;
  const heading = isCompany
    ? 'NPWP Bisnis dan KTP Pemilik/Penanggung Jawab'
    : 'KTP Pemilik/Penanggung Jawab';

  return (
    <>
      <Box
        as="h2"
        fontSize="24px"
        lineHeight="29px"
        fontWeight="medium"
        color="#373F41"
      >
        {heading}
      </Box>
      {afterHeading}
      <Box mt={4} display="flex" flexDirection="column" gap={4}>
        <DetailField label="Nama Bisnis" value={businessName} />
        <DetailField label="Alamat Bisnis" value={businessAddress} />
        <DetailField label="Penanggung Jawab" value={personName} />
        <DetailField label="Alamat Penanggung Jawab" value={personAddress} />
        <DetailField label="Email" value={email} />
        <DetailField label="Nomor Telepon" value={phone} />
      </Box>
    </>
  );
}

export function MobileBusinessDetailContent({
  business,
  verificationState,
}: {
  business: RegistrationBusinessProfile;
  verificationState: RegistrationVerificationState;
}) {
  return (
    <Stack spacing={6}>
      <BusinessVerificationDetailBody
        business={business}
        afterHeading={
          <Flex mt={3} justify="flex-end">
            <StatusBadge state={verificationState} />
          </Flex>
        }
      />
    </Stack>
  );
}
